<template>
  <footer :class="b()">
    <div :class="b('container')">
      <div :class="b('grid-container')">
        <div
          v-for="column in footerNavigation"
          :key="column.header"
          :class="b('column')"
        >
          <div
            :class="b('menu-heading')"
            @click="column.showMenu = !column.showMenu"
          >
            <div :class="b('menu-title')">
              {{ column.header }}
            </div>
            <e-icon
              :class="b('menu-title-icon', { active: column.showMenu })"
              icon="i-chevron--down"
              size="14"
            />
          </div>
          <div :class="b('menu-container', { show: column.showMenu })">
            <ul :class="b('menu-list')">
              <li
                v-for="menuItem in column.menu"
                :key="menuItem.id"
                :class="b('menu-item')"
              >
                <e-link
                  :class="b('menu-link')"
                  :link="menuItem"
                />
              </li>
            </ul>
          </div>
        </div>

        <div :class="b('column', { address: true })">
          <div :class="b('menu-heading')">
            <div :class="b('menu-title')">{{ footerContact.header }}</div>
          </div>

          <c-language-selector
            :class="b('language-selector')"
            overlay-position="top"
          />

          <div :class="b('content-block')">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="footerContact.content"></div>
          </div>
        </div>

        <div
          v-if="footerSocialLinks.items.length"
          :class="b('column', { social: true })"
        >
          <div :class="b('menu-heading')">
            <div :class="b('menu-title')">{{ footerSocialLinks.header }}</div>
          </div>

          <div :class="b('social-media-icons')">
            <e-social-media-icon
              v-for="(socialLink, index) in footerSocialLinks.items"
              :key="index"
              :link="socialLink.link"
              :image="socialLink.icon"
            />
          </div>
        </div>
      </div>
      <div :class="b('copyright')">
        {{ footerCopyrightText }} -
        <e-link
          :class="b('copyright-link')"
          :link="footerTermsLink"
        />
      </div>
    </div>
  </footer>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import useSessionStore from '@/stores/session';
  import cLanguageSelector from '@/components/c-language-selector.vue';
  import eIcon from '@/elements/e-icon.vue';
  import eLink from '@/elements/e-link.vue';
  import eSocialMediaIcon from '@/elements/e-social-media-icon.vue';
  import { Link } from '@/types/link';
  import { FooterNavigation } from '@/types/navigation';
  import { FooterContact, FooterSocialMediaLinks } from '@/types/session';

  type Setup = {
    sessionStore: ReturnType<typeof useSessionStore>;
  };
  // type Data = {};

  const MAX_MENU_COLUMNS = 4;

  export default defineComponent({
    name: 'c-footer',
    components: {
      eLink,
      eIcon,
      eSocialMediaIcon,
      cLanguageSelector,
    },

    // props: {},
    setup(): Setup {
      return {
        sessionStore: useSessionStore(),
      };
    },
    // data(): Data {
    //   return {};
    // },

    computed: {
      footerNavigation(): FooterNavigation[] {
        const { footerNavigation } = this.sessionStore;

        return footerNavigation.splice(0, MAX_MENU_COLUMNS);
      },

      footerSocialLinks(): FooterSocialMediaLinks {
        return this.sessionStore.footerSocialLinks;
      },

      footerContact(): FooterContact {
        return this.sessionStore.footerContact;
      },

      footerCopyrightText(): string {
        return this.sessionStore.footerCopyrightText;
      },

      footerTermsLink(): Link {
        return this.sessionStore.footerTermsLink;
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    // methods: {},
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/setup/headings';

  .c-footer {
    $this: &;

    background-color: var(--theme-color-grayscale--950);
    padding: 3rem 0;

    &__container {
      @include mixins.layout();

      max-width: 1200px;
    }

    &__grid-container {
      display: grid;
      grid-template-columns: 1fr;
      gap: 0.5rem;

      @include mixins.media($down: sm) {
        #{$this}__column--address,
        #{$this}__column--social {
          margin-top: 2rem;
          align-items: center;
          text-align: center;

          #{$this}__menu-heading {
            display: none;
          }
        }

        #{$this}__column--address {
          order: 2;
        }

        #{$this}__column--social {
          order: 1;
        }
      }

      // Larger Mobile to Tablet have two columns.
      @include mixins.media(sm) {
        grid-template-columns: 1fr 1fr;
        gap: 1.5rem;

        #{$this}__column--address,
        #{$this}__column--social {
          align-items: start;
          margin-top: 0;
          text-align: left;
          order: initial;
        }
      }

      // Tablet to smaller Desktop
      // Address and social are in a new line with 50% width.
      @include mixins.media(md) {
        grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
        grid-template-rows: auto auto;

        #{$this}__column--address,
        #{$this}__column--social {
          grid-row: 2;
          grid-column: span 2;
        }

        #{$this}__column--address {
          grid-column-start: 1;
        }

        #{$this}__column--social {
          grid-column-start: 3;
        }
      }

      // Desktop has as much columns as needed next to each other with the same width.
      @include mixins.media(lg) {
        grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
        grid-template-rows: unset;

        #{$this}__column--address,
        #{$this}__column--social {
          grid-row: unset;
          grid-column: unset;
          grid-column-start: unset;

          #{$this}__menu-heading {
            display: block;
          }
        }
      }
    }

    &__column {
      display: flex;
      flex-direction: column;
    }

    &__menu-heading {
      display: flex;
      justify-content: space-between;
      border-bottom: 2px solid var(--theme-color-grayscale--900);
      cursor: pointer;
      margin-bottom: 1rem;
      width: 100%;

      @include mixins.media(sm) {
        margin-bottom: 1.5rem;
        cursor: default;
      }
    }

    &__menu-container {
      display: none;
      margin-bottom: 1.5rem;

      @include mixins.media(sm) {
        margin-bottom: 0;
        display: block;
      }

      &--show {
        display: block;
      }
    }

    &__menu-title {
      @extend .headline-4;

      font-size: variables.$font-size--regular;
      line-height: 1.15;
      padding-right: 1.5rem;
      padding-bottom: 0.5rem;

      @include mixins.media(sm) {
        font-size: variables.$font-size--big;
        padding-bottom: 1.5rem;
      }
    }

    &__menu-title-icon {
      display: block;

      @include mixins.media(sm) {
        display: none;
      }

      &--active {
        transform: rotate(180deg);
      }
    }

    &__menu-item {
      display: flex;
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    &__menu-link {
      @include mixins.hover-effect--default;

      width: 100%;
    }

    &__language-selector {
      margin-bottom: 1.5rem;
    }

    &__content-block {
      margin-bottom: 0.5rem;
      line-height: 1.55;

      a {
        text-decoration: underline;
      }

      p,
      &:last-child {
        margin-bottom: 0;
      }
    }

    &__social-media-icons {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem 0.5rem;
    }

    &__copyright {
      border-top: 2px solid var(--theme-color-grayscale--900);
      margin-top: 2rem;
      padding: 2rem 0;
      text-align: center;
      font-size: variables.$font-size--small;
    }

    &__copyright-link {
      text-decoration: underline;
    }
  }
</style>
